import {
  Provider,
  ResourceType,
  type BackupVault,
  type InventoryResource,
} from '@repo/api-gw-sdk';

const SUPPORTED_RESOURCE_TYPES_BACKUP_TO_AZURE_VAULT = [
  ResourceType.AwsS3,
  ResourceType.GcpCloudStorageBucket,
  ResourceType.AwsEc2,
];

const CROSS_CLOUD_EXCLUDED_RESOURCE_TYPES: Partial<
  Record<Provider, Partial<Record<Provider, ResourceType[]>>>
> = {
  [Provider.Aws]: {
    [Provider.Azure]: [
      ResourceType.AwsRds,
      ResourceType.AwsEksNamespace,
      ResourceType.AwsDynamoDb,
      ResourceType.AwsFsx,
      ResourceType.AwsRedshift,
      ResourceType.AwsKeyspacesTable,
      ResourceType.AwsNeptune,
      ResourceType.AwsDocumentdb,
    ],
    [Provider.Gcp]: [
      ResourceType.AwsRds,
      ResourceType.AwsEksNamespace,
      ResourceType.AwsDynamoDb,
      ResourceType.AwsFsx,
      ResourceType.AwsRedshift,
      ResourceType.AwsKeyspacesTable,
      ResourceType.AwsNeptune,
      ResourceType.AwsDocumentdb,
    ],
  },
  [Provider.Gcp]: {
    [Provider.Azure]: [
      ResourceType.GcpGkeNamespace,
      ResourceType.GcpBigQuery,
      ResourceType.GcpCloudFirestore,
      ResourceType.GcpCloudSqlInstance,
    ],
    [Provider.Aws]: [
      ResourceType.GcpGkeNamespace,
      ResourceType.GcpBigQuery,
      ResourceType.GcpCloudFirestore,
      ResourceType.GcpCloudSqlInstance,
    ],
  },
  [Provider.Azure]: {
    [Provider.Gcp]: [
      ResourceType.AzureAksNamespace,
      ResourceType.AzureSqlDatabase,
      ResourceType.AzureSqlManagedInstance,
      ResourceType.AzurePostgresql,
      ResourceType.AzureMysql,
      ResourceType.AzureCosmosdbMongodb,
      ResourceType.AzureCosmosdbNosql,
      ResourceType.AzureSqlVirtualMachine,
      ResourceType.AzureSapHanaVm,
    ],
    [Provider.Aws]: [
      ResourceType.AzureAksNamespace,
      ResourceType.AzureSqlDatabase,
      ResourceType.AzureSqlManagedInstance,
      ResourceType.AzurePostgresql,
      ResourceType.AzureMysql,
      ResourceType.AzureCosmosdbMongodb,
      ResourceType.AzureCosmosdbNosql,
      ResourceType.AzureSqlVirtualMachine,
      ResourceType.AzureSapHanaVm,
    ],
  },
};

export interface CrossCloudVaultFlags {
  EnableAzureVaultNetworkIsolation?: boolean;
  EnableCrossCloudBackup?: boolean;
}

const validateCrossCloudBackupAllowed = (
  vaultCloudProvider: Provider,
  flags: CrossCloudVaultFlags
): boolean => {
  if (vaultCloudProvider === Provider.Azure) {
    return !flags.EnableAzureVaultNetworkIsolation;
  }

  return (
    !!flags.EnableCrossCloudBackup &&
    (vaultCloudProvider === Provider.Gcp || vaultCloudProvider === Provider.Aws)
  );
};

const getExcludedResourceTypesForCrossCloudBackup = (
  sourceCloudProvider: Provider,
  targetCloudProvider: Provider
): ResourceType[] =>
  CROSS_CLOUD_EXCLUDED_RESOURCE_TYPES[sourceCloudProvider]?.[
    targetCloudProvider
  ] || [];

const isVaultAllowedForResource = (
  resource: InventoryResource,
  vault: BackupVault,
  flags: CrossCloudVaultFlags
): boolean => {
  const vaultCloudProvider = vault.vaultAttributes.cloudProvider;
  const resourceCloudProvider = resource.cloudProvider;

  if (vaultCloudProvider === resourceCloudProvider) {
    return true;
  }

  if (resourceCloudProvider === Provider.MongoAtlas) {
    return true;
  }

  if (!validateCrossCloudBackupAllowed(vaultCloudProvider, flags)) {
    return false;
  }

  if (vaultCloudProvider === Provider.Azure) {
    return SUPPORTED_RESOURCE_TYPES_BACKUP_TO_AZURE_VAULT.includes(
      resource.resourceType
    );
  }

  const excludedTypes = getExcludedResourceTypesForCrossCloudBackup(
    resourceCloudProvider,
    vaultCloudProvider
  );
  return !excludedTypes.includes(resource.resourceType);
};

export const getEligibleProviderVaults = (
  vaults: BackupVault[],
  entities: InventoryResource[],
  flags: CrossCloudVaultFlags
): BackupVault[] => {
  if (!entities.length) {
    return [];
  }

  const sourceCloudProvider = entities[0].cloudProvider;

  return vaults
    .map((vault, index) => ({ vault, index }))
    .filter(
      ({ vault }) =>
        vault.status === 'ACTIVE' &&
        entities.every((resource) =>
          isVaultAllowedForResource(resource, vault, flags)
        )
    )
    .sort((a, b) => {
      const aNative =
        a.vault.vaultAttributes.cloudProvider === sourceCloudProvider;
      const bNative =
        b.vault.vaultAttributes.cloudProvider === sourceCloudProvider;

      if (aNative !== bNative) {
        return aNative ? -1 : 1;
      }

      return a.index - b.index;
    })
    .map(({ vault }) => vault);
};
